import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAllCollectedPIC } from "../../../../common/hooks/dataMapHooks";
import { SurveyAnswerFn, SurveyAnswers, useSurvey } from "../../../../common/hooks/api/useSurvey";
import { NetworkRequestState } from "../../../../common/models";
import { CategoryDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import {
  DataRetentionPolicyCategoryDto,
  DataRetentionPolicyDetailCategory,
  DataRetentionPolicyDto,
  RetentionOptions,
  UpdateDataRetentionPolicyDetailDto,
} from "../../../../common/service/server/dto/DataRetentionPolicyDto";
import { usePrimaryOrganizationId } from "../../../hooks";
import {
  CreateDataRentionPolicyFn,
  UpdateDataRentionPolicyFn,
  RemoveDataRetentionPolicyFn,
  useDataRetentionPolicy,
} from "../../../hooks/useDataRetention";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { Callout, CalloutVariant } from "../../Callout";
import { SurveyGateButton } from "../survey/SurveyGateButton";
import {
  DATA_RETENTION_CATEGORIES,
  DataRetentionCategoryQuestion,
  getDefaultDescription,
} from "./DataRetentionCategoryQuestion";
import { SurveyQuestion } from "../../get-compliant/survey/SurveyQuestion";
import { Button } from "@mui/material";
import { DataRetentionCategoryQuestionDisabled } from "./DataRetentionCategoryQuestionDisabled";

export const DATA_RETENTION_SURVEY_NAME = "data-retention-survey";

function isEnabledRetentionDetail(
  cat:
    | DataRetentionPolicyCategoryDto
    | {
        builtinType: DataRetentionPolicyDetailCategory;
        isRemoved: boolean;
      },
): cat is DataRetentionPolicyCategoryDto {
  return (
    (
      cat as {
        builtinType: DataRetentionPolicyDetailCategory;
        isRemoved: boolean;
      }
    ).isRemoved !== true
  );
}

export type UseDataRetentionModule = {
  requests: {
    request: NetworkRequestState;
    dataMapRequest: NetworkRequestState;
    dataRetentionRequest: NetworkRequestState;
  };
  isFinished: boolean;
  disabledTooltip?: string;
  updateRequest: NetworkRequestState;
  createRequest: NetworkRequestState;
  removeRequest: NetworkRequestState;
  saveDefaultDescriptions: () => Promise<void>;
  props: DataRetentionModuleProps;
};

export const useDataRetentionModule = (doneSlug?: string): UseDataRetentionModule => {
  const orgId = usePrimaryOrganizationId();

  const { answers, setAnswer, request } = useSurvey(orgId, DATA_RETENTION_SURVEY_NAME);

  const { data: dataMap, request: dataMapRequest } = useDataMap(orgId);

  const {
    onLoad,
    result: dataRetention,
    request: dataRetentionRequest,
    update: updateDataRetention,
    updateRequest,
    create: createDataRetention,
    createRequest,
    remove: removeDataRetention,
    removeRequest,
  } = useDataRetentionPolicy(orgId);

  const [approvedPurposes, setApprovedPurposes] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (onLoad) {
      setApprovedPurposes(() => {
        const res = new Set<string>();
        dataRetention?.details?.forEach((dto) => {
          if (dto.approved && (dto.retentionPeriod !== "CUSTOM" || dto.description)) {
            res.add(dto.id);
          }
        });
        return res;
      });
    }
  }, [onLoad]);

  const collectedPic = useAllCollectedPIC(dataMap);

  const unmappedItems = useMemo(() => {
    if (!dataRetention) {
      return [];
    }
    return collectedPic.filter(
      (pic) => !(dataRetention?.details ?? []).some((dr) => dr.pic.includes(pic.id)),
    );
  }, [collectedPic, dataRetention]);

  const anyMappedWithNoDescription = useMemo(
    () =>
      !dataRetention
        ? false
        : (dataRetention?.details ?? []).some(
            (dr) => dr.pic.length > 0 && !dr.description && dr.approved,
          ),
    [dataRetention],
  );

  const hasDoneButton = Boolean(doneSlug);

  const doneButtonFinished = !hasDoneButton || answers[doneSlug] === "true";

  const isFinished = useMemo(() => {
    const allApproved = dataRetention?.details?.every(({ id }) => approvedPurposes.has(id));
    return (
      allApproved && doneButtonFinished && unmappedItems.length === 0 && !anyMappedWithNoDescription
    );
  }, [
    approvedPurposes,
    unmappedItems,
    doneButtonFinished,
    anyMappedWithNoDescription,
    dataRetention,
  ]);

  const doneButton = hasDoneButton ? { slug: doneSlug } : undefined;
  let disabledTooltip = null;
  if (unmappedItems.length === 0) {
    disabledTooltip = "Save all retention periods before proceeding.";
  }

  const saveDefaultDescriptions = useCallback(async () => {
    const details = dataRetention?.details ?? [];
    const saves = details
      .filter((detail) => !detail.description && detail.builtinType)
      .map((detail) => {
        const defaultDescription = getDefaultDescription(detail);
        if (!defaultDescription) return null;
        return updateDataRetention({
          id: detail.id,
          dto: {
            description: defaultDescription,
            customLabel: detail.customLabel ?? null,
            retentionPeriod: detail.retentionPeriod,
            pic: detail.pic,
            approved: detail.approved,
          },
        });
      })
      .filter(Boolean);
    await Promise.all(saves);
  }, [dataRetention, updateDataRetention]);

  return {
    requests: {
      request,
      dataMapRequest,
      dataRetentionRequest,
    },
    isFinished,
    disabledTooltip,
    updateRequest,
    createRequest,
    removeRequest,
    saveDefaultDescriptions,
    props: {
      collectedPic,
      dataRetention,
      unmappedItems,
      updateDataRetention,
      createDataRetention,
      removeDataRetention,
      answers,
      setAnswer,
      doneButton,
      setApproved: (purposeKey, approved) => {
        setApprovedPurposes((old) => {
          if (approved) {
            old.add(purposeKey);
          } else {
            old.delete(purposeKey);
          }
          return new Set(old);
        });
      },
    },
  };
};

export type DataRetentionSurveyDoneButton = {
  slug: string;
};
export type DataRetentionModuleProps = {
  collectedPic: CategoryDto[];
  dataRetention: DataRetentionPolicyDto;
  unmappedItems: CategoryDto[];
  updateDataRetention: UpdateDataRentionPolicyFn;
  createDataRetention: CreateDataRentionPolicyFn;
  removeDataRetention: RemoveDataRetentionPolicyFn;
  answers: SurveyAnswers;
  setAnswer: SurveyAnswerFn;
  doneButton?: DataRetentionSurveyDoneButton;
  setApproved: (retentionKey: string, approved: boolean) => void;
};
export const DataRetentionModule: React.FC<DataRetentionModuleProps> = ({
  collectedPic,
  dataRetention,
  answers,
  setAnswer,
  updateDataRetention,
  createDataRetention,
  removeDataRetention,
  unmappedItems,
  doneButton,
  setApproved,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const dataRetentionDetails = dataRetention?.details ?? [];

  const sortedDetails: (
    | DataRetentionPolicyCategoryDto
    | {
        builtinType: DataRetentionPolicyDetailCategory;
        isRemoved: boolean;
      }
  )[] = [
    ...Object.keys(DATA_RETENTION_CATEGORIES)
      .filter(
        (cat) =>
          !dataRetentionDetails.some((retentionCategory) => cat == retentionCategory.builtinType),
      )
      .map((builtinType) => ({
        builtinType: builtinType as DataRetentionPolicyDetailCategory,
        isRemoved: true,
      })),
    ...dataRetentionDetails,
  ].sort((a, b) => {
    const left = DATA_RETENTION_CATEGORIES[a.builtinType]?.displayOrder ?? 1000;
    const right = DATA_RETENTION_CATEGORIES[b.builtinType]?.displayOrder ?? 1000;
    return left - right;
  });

  const warningCallout = (
    <>
      {unmappedItems.length > 0 && (
        <Callout variant={CalloutVariant.Yellow}>
          <p>
            The following personal information categories were not selected for a retention policy.
            To continue please ensure that all categories are selected for at least one retention
            policy above.
          </p>

          <ul>
            {unmappedItems.map((unmappedPic) => (
              <li key={unmappedPic.id}>
                <strong>{unmappedPic.name}</strong>
              </li>
            ))}
          </ul>
        </Callout>
      )}
    </>
  );

  return (
    <>
      <div>
        <SurveyQuestion
          question={{
            question: "Data Retention Policy Intro Statement",
            slug: "retention-policy",
            type: "freetext",
            wysiwyg: true,
            visible: true,
            rows: 3,
            toolbar: false,
            showSaveButton: true,
          }}
          answer={answers["retention-policy"]}
          onAnswer={(ans) => setAnswer("retention-policy", ans)}
        />
      </div>

      <div className="mt-xl">
        {sortedDetails.map((detail) => {
          if (isEnabledRetentionDetail(detail)) {
            return (
              <DataRetentionCategoryQuestion
                key={detail.id}
                picOptions={collectedPic}
                detail={detail}
                onUpdate={(dto: UpdateDataRetentionPolicyDetailDto) => {
                  updateDataRetention({
                    id: detail.id,
                    dto,
                  });
                }}
                setApproved={setApproved}
                onRemove={() => {
                  removeDataRetention({ id: detail.id });
                }}
              />
            );
          } else {
            const canSetRetention =
              DATA_RETENTION_CATEGORIES[detail.builtinType].description?.includes("YEARS");
            const defaultRetentionPeriod: RetentionOptions = canSetRetention ? "YEARS_7" : "CUSTOM";
            return (
              <DataRetentionCategoryQuestionDisabled
                key={detail.builtinType}
                category={detail.builtinType}
                onRestore={() => {
                  createDataRetention({
                    dto: {
                      builtinType: detail.builtinType,
                      description: "",
                      customLabel: null,
                      retentionPeriod: defaultRetentionPeriod,
                      pic: [],
                      approved: false,
                    },
                  });
                }}
              />
            );
          }
        })}

        {isAdding && (
          <DataRetentionCategoryQuestion
            picOptions={collectedPic}
            detail={null}
            onUpdate={() => {}}
            onCreate={(dto) => {
              createDataRetention({ dto });
            }}
            onCreated={() => setIsAdding(false)}
          />
        )}

        {doneButton ? (
          <>
            <SurveyGateButton
              slug={doneButton.slug}
              survey={answers}
              updateResponse={setAnswer}
              label="Done with Review"
            >
              <div className="mb-md">{warningCallout}</div>
            </SurveyGateButton>
            <Button
              color="primary"
              variant="contained"
              disabled={isAdding}
              onClick={() => setIsAdding(true)}
            >
              Add Retention Purpose
            </Button>
          </>
        ) : (
          <>
            <div className="mb-md">{warningCallout}</div>
            <Button
              color="primary"
              variant="contained"
              disabled={isAdding}
              onClick={() => setIsAdding(true)}
            >
              Add Retention Purpose
            </Button>
          </>
        )}
      </div>
    </>
  );
};
